import type { CRMUser, EmployeeDesignation, ExecutiveBriefingProfile, User } from '../types';
import type { HandbookChangeRequestStatus } from '../types/handbookPolicy';
import { isPeopleHubUserRole } from './employeeBenefitsAccess';

/** CEO briefing profile from auth user or CRM hydration. */
export function resolveHandbookBriefingProfile(
  user: User | null | undefined,
  crmUsers?: CRMUser[] | null,
): ExecutiveBriefingProfile | undefined {
  const fromAuth = user?.executiveBriefing;
  const fromCrm = crmUsers?.find(u => u.uid === user?.id)?.executiveBriefing;
  if (fromAuth?.profile === 'ceo' || fromCrm?.profile === 'ceo') return 'ceo';
  if (fromAuth?.enabled && fromAuth.profile === 'coo') return 'coo';
  if (fromCrm?.enabled && fromCrm.profile === 'coo') return 'coo';
  const inbox = crmUsers?.find(u => u.uid === user?.id)?.execInbox;
  if (inbox?.enabled) return 'ceo';
  return fromAuth?.profile || fromCrm?.profile;
}

/** People Hub employees may open a change request against a published policy. */
export function canProposeHandbookChange(args: {
  authRole: string | undefined;
  designations?: EmployeeDesignation[] | string[];
}): boolean {
  return isPeopleHubUserRole(args.authRole, args.designations);
}

/** CEO (admin or briefing profile ceo) may approve, request changes, or reject. */
export function canApproveHandbookChange(args: {
  authRole: string | undefined;
  briefingProfile?: ExecutiveBriefingProfile | null;
}): boolean {
  if (args.authRole === 'admin') return true;
  return args.briefingProfile === 'ceo';
}

/** @deprecated Use canProposeHandbookChange / canApproveHandbookChange. */
export function canEditHandbookPolicies(args: {
  authRole: string | undefined;
  designations?: EmployeeDesignation[] | string[] | undefined;
  briefingProfile?: ExecutiveBriefingProfile | null;
}): boolean {
  return canApproveHandbookChange({
    authRole: args.authRole,
    briefingProfile: args.briefingProfile,
  });
}

export function nextHandbookPolicyNumber(policyNumbers: string[]): string {
  let max = 0;
  for (const raw of policyNumbers) {
    const n = parseInt(String(raw).replace(/\D/g, ''), 10);
    if (!Number.isNaN(n) && n > max) max = n;
  }
  return String(max + 1).padStart(2, '0');
}

export function bumpHandbookVersion(current: string): string {
  const cleaned = String(current || '')
    .trim()
    .replace(/^v/i, '');
  if (!cleaned) return '1.0';
  const parts = cleaned.split('.');
  const major = parseInt(parts[0], 10);
  if (Number.isNaN(major) || major < 1) return '1.0';
  if (parts.length === 1) return `${major}.1`;
  const minor = parseInt(parts[1], 10);
  if (Number.isNaN(minor) || minor < 0) return `${major}.1`;
  return `${major}.${minor + 1}`;
}

export function canAuthorEditChangeRequest(status: HandbookChangeRequestStatus): boolean {
  return status === 'draft' || status === 'changes_requested';
}

export function canAuthorSubmitChangeRequest(status: HandbookChangeRequestStatus): boolean {
  return status === 'draft' || status === 'changes_requested';
}

export function canAuthorWithdrawChangeRequest(status: HandbookChangeRequestStatus): boolean {
  return status === 'draft' || status === 'open' || status === 'changes_requested';
}

/** Author may delete their own CR; CEO may delete any. */
export function canDeleteHandbookChangeRequest(args: {
  status: HandbookChangeRequestStatus;
  authorUid: string;
  actorUid: string | undefined;
  isCeo: boolean;
}): boolean {
  if (!args.actorUid) return false;
  if (args.isCeo) return true;
  return args.authorUid === args.actorUid;
}

export function canCeoReviewChangeRequest(status: HandbookChangeRequestStatus): boolean {
  return status === 'open';
}

export function handbookChangeRequestIsActive(status: HandbookChangeRequestStatus): boolean {
  return status === 'draft' || status === 'open' || status === 'changes_requested';
}
